import { ref, type Ref } from 'vue';
import type { PaginationLinks, PaginationMetadata } from '@/types/browser';
import { usePaginatedData } from '@/composables/usePaginatedData';

export interface Page<T> {
  metadata: PaginationMetadata;
  links: PaginationLinks;
  results: T[];
}

/**
 * A list fetched a page at a time, in which only the latest request counts.
 *
 * `fetchPage(null)` asks for the first page of whatever the caller's filters describe at that
 * moment. A page url is different: it carries the query of the fetch that produced it.
 *
 * Responses can land out of order — a chip removed while a search is still in flight starts a
 * second request — so only the latest request may fill the list or switch the spinner off. That
 * makes `loading` mean "the latest request is still out": the list on screen is about to be
 * replaced, which is what a pager has to wait for (see Pagination.vue).
 *
 * A request that fails answers with nothing and leaves the list as it was, rows and page links
 * alike — so after a failed filter change they still belong to the query before it. "Nothing" is
 * whatever the caller's fetch resolves to when it has no page: undefined from one that reports
 * its own failure, null from one that could not ask at all (fetchSystemCharacters, with no
 * system).
 *
 * This is the part every paged list shares. A list with a search box adds a search that waits for
 * a pause (useSearchableList), a browser its opening and closing on top of that (useBrowserList),
 * and a grid its predicate watcher (useElementGrid).
 */
export function usePagedList<T>(fetchPage: (url: string | null) => Promise<Page<T> | null | undefined | void>) {
  const dataItems = ref<T[]>([]) as Ref<T[]>;
  const loading = ref(false);
  const { metadata, links, updatePagination } = usePaginatedData();

  let latestFetch = 0;
  // What the latest request asked for: null for a first page, otherwise the page's url.
  let latestUrl: string | null = null;

  const fetch = async (url: string | null = null) => {
    const sequence = ++latestFetch;
    latestUrl = url;
    loading.value = true;
    try {
      const data = await fetchPage(url);
      if (sequence !== latestFetch) return;
      // A fetch that resolves to nothing (its caller handled an error) leaves the list as it was.
      if (data) {
        dataItems.value = data.results;
        updatePagination(data);
      }
    } finally {
      if (sequence === latestFetch) loading.value = false;
    }
  };

  // Asks again for the page on screen — or, while a request is out, for what that request asked.
  // The page on screen is then on its way out, and its url carries the query it was fetched with:
  // refreshing it would win as the newest request and bring the old filter's page back under the
  // new filter's chips.
  const refresh = () => fetch(loading.value ? latestUrl : links.value.current);

  const onNextPage = async () => {
    if (links.value.next) await fetch(links.value.next);
  };

  const onPreviousPage = async () => {
    if (links.value.previous) await fetch(links.value.previous);
  };

  return { dataItems, loading, metadata, links, fetch, refresh, onNextPage, onPreviousPage };
}
